import React, { useState } from 'react';
import { toast } from 'bulma-toast';
import PropTypes from 'prop-types';
import styles from './styles.scss';
import ModalContainer from '../../../molecules/modal-container';
import MetaPixelCreateControl from './MetaPixelCreateControl';
import { Button } from '../../../atoms/button/index.ts';

const MetaPixelCreateForm = function MetaPixelCreateForm({ pixelId, domain }) {
    const [show, setShow] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const [fields, setFields] = useState({
        pixelId, domain, token: '', permission: false,
    });

    // We need to tell the parent page to set up the modal background
    // because the contents of an iframe cannot extend or access the parent page.
    window.parent.postMessage(['modalContainer', show], '*');

    const successToast = () => toast({
        position: 'bottom-center',
        duration: 5000,
        closeOnClick: false,
        pauseOnHover: true,
        type: 'is-info is-light',
        message: 'Facebook Pixel has been successfully updated!',
    });

    const submitHandler = () => {
        const { protocol, host } = new URL(window.location.href);
        const query = `${protocol}//${host}/api/pixel/create`;

        const formData = new FormData();
        formData.append('pixelId', fields.pixelId);
        formData.append('accessToken', fields.token);
        formData.append('domain', fields.domain);

        fetch(query, { method: 'POST', body: formData })
            .then((response) => {
                if (response.ok) {
                    setIsSubmitting(false);
                    setError('');
                    successToast();
                    return;
                }

                if (response.status === 401) {
                    window.parent.postMessage(['redirectToLogin'], '*');
                    return;
                }

                setIsSubmitting(false);
                throw Error(response.statusText);
            })
            .catch((err) => {
                setIsSubmitting(false);
                setError(`Something went wrong. Please try again later - ${err}`);
            });

        setIsSubmitting(true);
    };

    const checkEmptyFields = () => Object.entries(fields).find((f) => !f[1]);
    const handleFieldInput = (e) => setFields({ ...fields, [e.target.name]: e.target.value });

    return (
        <>
            <Button id="connect-pixel" appearance="primary" onClick={() => setShow(true)}>
                {pixelId ? 'Edit Facebook' : 'Connect Facebook'}
            </Button>
            <ModalContainer
                modaltitle="Facebook Pixel"
                modalclosebuttontext=""
                confirmbuttontext=""
                showheader
                showmodal={show}
                toggleshow={setShow}
                modalcardstyle={{ width: '60%' }}
            >
                <div className={styles.error}>{error}</div>
                <MetaPixelCreateControl name="pixelId" label="Pixel ID" placeholder="e.g. 1154536292XXXXXX" type="text" value={fields.pixelId} onChange={handleFieldInput} />
                <MetaPixelCreateControl name="domain" label="Verified Domain" placeholder="e.g. giveeasy.org" value={fields.domain} onChange={handleFieldInput} prefix={<span className={styles['https-prefix']}>https://</span>} />
                <MetaPixelCreateControl name="token" label="Access Token (Conversion API)" value={fields.token} onChange={handleFieldInput} />
                <MetaPixelCreateControl key="permission" label="I have permission to send data to Facebook" isCheckbox value={fields.permission} onChange={() => setFields({ ...fields, permission: !fields.permission })} />
                <div className={styles['help-link-container']}>
                    <a className={styles['help-link']} href="https://giveeasy.zendesk.com/hc/en-us/articles/5403057659535">
                        For more information on Facebook Pixel and instructions, click here
                    </a>
                </div>
                <div className={styles['submit-container']}>
                    <div className={styles['submit-positioner']}>
                        <Button
                            type="submit"
                            id="confirm-pixel-details"
                            appearance="primary"
                            onClick={submitHandler}
                            isLoading={isSubmitting}
                            disabled={!!checkEmptyFields() || isSubmitting}
                        >
                            Confirm
                        </Button>
                    </div>
                </div>
            </ModalContainer>
        </>
    );
};

MetaPixelCreateForm.propTypes = {
    pixelId: PropTypes.string,
    domain: PropTypes.string,
};

MetaPixelCreateForm.defaultProps = {
    pixelId: '',
    domain: '',
};

export default MetaPixelCreateForm;
